import React from 'react';
import Head from 'next/head';
import { SITE_NAME, SITE_LOGO } from '@src/constants/site';
import { getPageUrlWithBaseURL } from '@src/utils/url';
import {
  GlobalSettingItemFragment,
  PageCommunityDetail,
  PageCommunityNews,
  PageNationalNews
} from '@src/graphql/gql-types';
import { getGlobalContactPhoneNumberEntry } from '@src/utils/globalSettings';

interface SEOProps {
  metaTitle?: string | undefined;
  metaPageUrl: string;
  metaDesc?: string;
  metaKeywords?: string;
  metaOgTitle?: string;
  metaOgImage?: string;
  metaOgImageAlt?: string;
  noIndex?: boolean | null | undefined;
  noFollow?: boolean | null | undefined;
  seoRedirect?: string;
  canonicalUrl?: string;
  isNationalPage?: boolean;
  communityDetailsPageData?: PageCommunityDetail | undefined;
  newsPageData?: PageCommunityNews | PageNationalNews | undefined;
  globalSettingItems?: GlobalSettingItemFragment[];
}

// Google Search - structured data
const ldJsonData = {
  __html: JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: 'Levande Living',
    url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
  })
};

const SEO: React.FC<SEOProps> = ({
  metaTitle = 'Levande | A new definition of lifestyle living',
  metaPageUrl = process.env.NEXT_PUBLIC_BASE_URL,
  metaDesc,
  metaKeywords,
  metaOgTitle,
  metaOgImage = getPageUrlWithBaseURL(SITE_LOGO),
  metaOgImageAlt,
  noIndex,
  noFollow,
  seoRedirect,
  canonicalUrl,
  isNationalPage,
  communityDetailsPageData,
  newsPageData,
  globalSettingItems
}) => {
  const fullPageUrl = getPageUrlWithBaseURL(metaPageUrl as string);
  const fullPageCanonicalUrl = canonicalUrl
    ? getPageUrlWithBaseURL(canonicalUrl)
    : undefined;
  const metaDescription = metaDesc ? metaDesc : metaTitle;
  const metaRobots = [];

  const nationalStandardLdJsonData = {
    __html: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Levande',
      url: process.env.NEXT_PUBLIC_BASE_URL,
      logo: `${process.env.NEXT_PUBLIC_BASE_URL}/assets/logo.png`,
      sameAs: [
        'https://www.youtube.com/channel/UC_7guVD9onDcJTSRu5K5P9g',
        'https://www.instagram.com/levandeliving/',
        'https://www.facebook.com/LevandeLiving/',
        'https://www.linkedin.com/company/levande-living/'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: globalSettingItems
          ? `+61-${getGlobalContactPhoneNumberEntry(globalSettingItems)?.value}`
          : '+61-1800 72 71 70',
        contactType: 'Customer Service',
        areaServed: 'AU',
        availableLanguage: 'English'
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Level 18, 9 Castlereagh Street',
        addressLocality: 'Sydney',
        addressRegion: 'NSW',
        postalCode: '2000',
        addressCountry: 'AU'
      }
    })
  };

  let communityLdJsonData;
  if (communityDetailsPageData) {
    const streetAddress =
      communityDetailsPageData?.communityAddress?.split(', ')?.[0];
    const addressDetails = communityDetailsPageData?.communityAddress
      ?.split(', ')?.[1]
      ?.split(' ');
    const postcode = addressDetails?.[addressDetails.length - 1];
    const addressRegion = addressDetails?.[addressDetails.length - 2];
    const addressLocality = addressDetails?.slice(0, -2)?.join(' ');

    const communityLdJson = {
      '@context': 'http://schema.org',
      '@type': 'LocalBusiness',
      name: communityDetailsPageData?.title,
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/community/${communityDetailsPageData?.slug}`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: streetAddress,
        addressLocality: addressLocality,
        addressRegion: addressRegion,
        postalCode: postcode,
        addressCountry: 'AU'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: communityDetailsPageData?.latLon?.lat,
        longitude: communityDetailsPageData?.latLon?.lon
      },
      telephone:
        communityDetailsPageData?.contactSalesRepresentative?.phoneNumber,
      email: communityDetailsPageData?.contactSalesRepresentative?.email,
      image: [metaOgImage],
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '16:00'
        }
      ]
    };

    communityLdJsonData = {
      __html: JSON.stringify(communityLdJson)
    };
  }

  let newsLdJsonData;
  if (newsPageData) {
    newsLdJsonData = {
      __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: newsPageData?.title,
        author: {
          '@type': 'Person',
          name: 'Levande'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Levande'
        }
      })
    };
  }

  if (noIndex || !!seoRedirect) {
    metaRobots.push('noindex');
  } else {
    metaRobots.push('index');
  }
  if (noFollow) {
    metaRobots.push('nofollow');
  } else {
    metaRobots.push('follow');
  }

  return (
    <Head key="seo-head">
      <title>{metaTitle}</title>
      <link rel="icon" href="/favicon.ico" />
      <link rel="canonical" href={fullPageCanonicalUrl || fullPageUrl} />

      {!!seoRedirect && (
        <meta httpEquiv="refresh" content={`0;URL='${seoRedirect}'`} />
      )}
      {!!metaRobots.length && (
        <meta name="robots" content={metaRobots.join(',')} />
      )}

      <meta name="description" content={metaDescription} />

      {!!metaKeywords && <meta name="keywords" content={metaKeywords} />}

      {/* Twitter Card data */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaOgImage} />

      {/* Open Graph data */}
      <meta
        property="og:title"
        content={metaOgTitle ? metaOgTitle : metaTitle}
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullPageUrl} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={metaOgImage as string} />
      <meta property="og:image:secure_url" content={metaOgImage as string} />
      {metaOgImageAlt && (
        <meta property="og:image:alt" content={metaOgImageAlt as string} />
      )}

      <script type="application/ld+json" dangerouslySetInnerHTML={ldJsonData} />
      {isNationalPage && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={nationalStandardLdJsonData}
        />
      )}
      {communityDetailsPageData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={communityLdJsonData}
        />
      )}
      {newsPageData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={newsLdJsonData}
        />
      )}
    </Head>
  );
};

export default SEO;
